// SPDX-License-Identifier: AGPL-3.0-or-later

import { useState, useEffect, type FormEvent } from 'react';
import { Button, Card, FormError, Input, Label, Stack } from '@intakeform/ui';
import { useMe } from '../../api/auth.js';
import { usePatchOrg } from '../../api/customer-enablement.js';
import { OrgThemeStyles } from '../../theme/index.js';

const COLOR_FAMILIES = [
  'neutral',
  'primary',
  'accent',
  'info',
  'success',
  'warning',
  'danger',
] as const;
type ColorFamily = (typeof COLOR_FAMILIES)[number];

const FAMILY_VARIANTS = ['', '-foreground', '-muted', '-subtle'] as const;

const BRAND_RADII = ['none', 'sm', 'md', 'lg', 'full'] as const;
type BrandRadius = (typeof BRAND_RADII)[number];
function isBrandRadius(v: string): v is BrandRadius {
  return (BRAND_RADII as readonly string[]).includes(v);
}

const SURFACE_TOKENS = [
  'background',
  'surface',
  'surface-raised',
  'surface-sunken',
  'foreground',
  'muted-foreground',
  'border',
  'border-strong',
  'ring',
  'flagged',
  'flagged-subtle',
] as const;

const SHADOW_TOKENS = [
  { name: 'sm', label: 'sm — list rows, subtle lifts' },
  { name: 'md', label: 'md — cards, default elevation' },
  { name: 'lg', label: 'lg — modals, popovers' },
  { name: 'xl', label: 'xl — full-screen overlays' },
  { name: 'inner', label: 'inner — pressed surfaces' },
] as const;

const RADIUS_TOKENS = [
  { name: 'sm', label: '8 px — inputs, chips' },
  { name: 'md', label: '12 px — buttons, cards' },
  { name: 'lg', label: '16 px — large cards' },
  { name: 'xl', label: '24 px — heroes' },
  { name: 'full', label: 'pill / circle' },
] as const;

const TYPE_SCALE = [
  { token: '3xl', label: 'Hero — login, marketing surfaces' },
  { token: '2xl', label: 'Page heading' },
  { token: 'xl', label: 'Display section' },
  { token: 'lg', label: 'Card title' },
  { token: 'base', label: 'Body copy' },
  { token: 'sm', label: 'Helper / label' },
  { token: 'xs', label: 'Caption / micro' },
] as const;

const GRADIENT_TOKENS = [
  { name: 'subtle-warm', label: 'Background wash — main shell on light mode' },
  { name: 'hero', label: 'Hero — login + landing' },
  { name: 'primary', label: 'Primary CTA — pressable surfaces' },
  { name: 'accent', label: 'Accent — delight surfaces only' },
] as const;

interface OrgSettingsShape {
  brand_name?: string;
  brand_color?: string;
  brand_radius?: BrandRadius;
}

export function DesignSystemRoute() {
  const { data: me } = useMe();
  const patch = usePatchOrg();
  const isAdmin = me?.user.role === 'admin';

  const [activeTab, setActiveTab] = useState<'components' | 'tokens'>('components');

  const [brandName, setBrandName] = useState('');
  const [brandColor, setBrandColor] = useState('#1d4ed8');
  const [brandRadius, setBrandRadius] = useState<BrandRadius>('md');
  const [livePreview, setLivePreview] = useState(true);

  // Sync state once organization settings load
  useEffect(() => {
    if (me) {
      const settings = me.organization.settings as OrgSettingsShape | undefined;
      setBrandName(settings?.brand_name ?? me.organization.name ?? '');
      setBrandColor(settings?.brand_color ?? '#1d4ed8');
      setBrandRadius(settings?.brand_radius ?? 'md');
    }
  }, [me]);

  const onSaveBranding = (e: FormEvent) => {
    e.preventDefault();
    patch.mutate({
      settings: {
        brand_name: brandName,
        brand_color: brandColor,
        brand_radius: brandRadius,
      },
    });
  };

  return (
    <div className="space-y-6">
      {/* Live Customizer Preview CSS injection */}
      {livePreview && (
        <OrgThemeStyles settings={{ brand_color: brandColor, brand_radius: brandRadius }} />
      )}

      {/* Brand Customizer Form Card */}
      {isAdmin && (
        <Card heading="Clinic Branding Customizer (Admin)" variant="raised" tone="accent">
          <p className="text-sm text-muted-foreground mb-4">
            Customize the look and feel of the application to match your clinic's brand. Changes
            saved here will instantly apply to the clinician dashboard and patient intake forms.
          </p>
          <form onSubmit={onSaveBranding} className="space-y-4 max-w-xl">
            <Stack gap={16}>
              <Stack gap={4}>
                <Label htmlFor="custom-brand-name">Clinic Brand Name</Label>
                <Input
                  id="custom-brand-name"
                  type="text"
                  value={brandName}
                  onChange={(e) => setBrandName(e.currentTarget.value)}
                  placeholder="e.g. Acme Health Clinic"
                />
              </Stack>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Stack gap={4}>
                  <Label htmlFor="custom-brand-color">Brand Primary Color</Label>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input
                      id="custom-brand-color-picker"
                      type="color"
                      value={brandColor}
                      onChange={(e) => setBrandColor(e.currentTarget.value)}
                      style={{
                        width: 44,
                        height: 44,
                        border: '1px solid rgb(var(--color-border))',
                        borderRadius: 'var(--radius-sm)',
                        padding: 0,
                        cursor: 'pointer',
                        background: 'transparent',
                      }}
                    />
                    <Input
                      id="custom-brand-color"
                      type="text"
                      pattern="^#[0-9a-fA-F]{6}$"
                      value={brandColor}
                      onChange={(e) => setBrandColor(e.currentTarget.value)}
                      placeholder="#1d4ed8"
                      style={{ flex: 1 }}
                    />
                  </div>
                </Stack>

                <Stack gap={4}>
                  <Label htmlFor="custom-brand-radius">Corner Rounding (Radius)</Label>
                  <select
                    id="custom-brand-radius"
                    value={brandRadius}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (isBrandRadius(v)) setBrandRadius(v);
                    }}
                    className="w-full px-3 py-2 text-sm bg-surface border border-border rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-primary"
                  >
                    <option value="none">None (Sharp corners)</option>
                    <option value="sm">Subtle (sm)</option>
                    <option value="md">Standard (md)</option>
                    <option value="lg">Soft (lg)</option>
                    <option value="full">Pill / Circular (full)</option>
                  </select>
                </Stack>
              </div>

              <div className="flex items-center gap-2 mt-2">
                <input
                  id="enable-live-preview"
                  type="checkbox"
                  checked={livePreview}
                  onChange={(e) => setLivePreview(e.currentTarget.checked)}
                  style={{ cursor: 'pointer' }}
                />
                <Label htmlFor="enable-live-preview" style={{ margin: 0, cursor: 'pointer' }}>
                  Enable real-time customizer preview
                </Label>
              </div>

              {patch.error && (
                <div className="error-banner text-sm" role="alert">
                  {patch.error.message}
                </div>
              )}

              {patch.isSuccess && (
                <div className="text-sm text-success font-medium">
                  Branding saved and published successfully!
                </div>
              )}

              <div className="flex gap-3">
                <Button type="submit" variant="primary" disabled={patch.isPending}>
                  {patch.isPending ? 'Publishing…' : 'Publish Brand Theme'}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    const settings = me?.organization?.settings as OrgSettingsShape | undefined;
                    setBrandName(settings?.brand_name ?? me?.organization?.name ?? '');
                    setBrandColor(settings?.brand_color ?? '#1d4ed8');
                    setBrandRadius(settings?.brand_radius ?? 'md');
                  }}
                >
                  Reset to Saved
                </Button>
              </div>
            </Stack>
          </form>
        </Card>
      )}

      {/* Local Tab Toggle */}
      <div
        style={{
          display: 'flex',
          gap: '8px',
          borderBottom: '1px solid rgb(var(--color-border))',
          paddingBottom: '8px',
          marginBottom: '24px',
        }}
      >
        <button
          type="button"
          onClick={() => setActiveTab('components')}
          style={{
            padding: '8px 16px',
            fontSize: 'var(--text-sm)',
            fontWeight: 'var(--weight-semibold)',
            color:
              activeTab === 'components'
                ? 'rgb(var(--color-primary))'
                : 'rgb(var(--color-muted-foreground))',
            borderBottom:
              activeTab === 'components'
                ? '2px solid rgb(var(--color-primary))'
                : '2px solid transparent',
            marginBottom: '-9px',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
          }}
        >
          UI Components
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('tokens')}
          style={{
            padding: '8px 16px',
            fontSize: 'var(--text-sm)',
            fontWeight: 'var(--weight-semibold)',
            color:
              activeTab === 'tokens'
                ? 'rgb(var(--color-primary))'
                : 'rgb(var(--color-muted-foreground))',
            borderBottom:
              activeTab === 'tokens'
                ? '2px solid rgb(var(--color-primary))'
                : '2px solid transparent',
            marginBottom: '-9px',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
          }}
        >
          Design Tokens
        </button>
      </div>

      {activeTab === 'components' ? (
        <Stack gap={32}>
          {/* Buttons */}
          <section aria-labelledby="ds-buttons-h">
            <Card heading="Buttons" variant="default">
              <p className="text-sm text-muted-foreground mb-4">
                Interactive button components for user actions.
              </p>
              <Stack direction="row" gap={12} style={{ flexWrap: 'wrap' }}>
                <Button variant="primary">Primary</Button>
                <Button variant="secondary">Secondary</Button>
                <Button variant="ghost">Ghost</Button>
                <Button variant="danger">Danger</Button>
                <Button variant="accent">Accent</Button>
                <Button variant="primary" disabled>
                  Disabled
                </Button>
              </Stack>
            </Card>
          </section>

          {/* Input + Label */}
          <section aria-labelledby="ds-input-h">
            <Card heading="Inputs & Form Fields" variant="default">
              <p className="text-sm text-muted-foreground mb-4">
                Standard text input, labels, and validation feedback.
              </p>
              <Stack gap={16} style={{ maxWidth: 360 }}>
                <Stack gap={4}>
                  <Label htmlFor="demo-name" required>
                    Full name
                  </Label>
                  <Input id="demo-name" placeholder="Alex Patient" />
                </Stack>
                <Stack gap={4}>
                  <Label htmlFor="demo-bad">Phone</Label>
                  <Input id="demo-bad" aria-invalid="true" defaultValue="not-a-phone" />
                  <FormError
                    findings={[
                      {
                        severity: 'error',
                        code: 'regex',
                        message: 'Enter a phone number in E.164 format.',
                      },
                    ]}
                  />
                </Stack>
                <Stack gap={4}>
                  <Label htmlFor="demo-disabled">Disabled</Label>
                  <Input id="demo-disabled" disabled defaultValue="readonly" />
                </Stack>
              </Stack>
            </Card>
          </section>

          {/* Cards */}
          <section aria-labelledby="ds-card-h">
            <Card heading="Cards & Surfaces" variant="default">
              <p className="text-sm text-muted-foreground mb-4">
                Content containers representing different visual states and informational tones.
              </p>
              <Stack direction="row" gap={16} style={{ flexWrap: 'wrap' }}>
                <Card heading="Default" style={{ minWidth: 220 }}>
                  Default card surface.
                </Card>
                <Card heading="Raised" variant="raised" style={{ minWidth: 220 }}>
                  Elevated above the default surface.
                </Card>
                <Card heading="Elevated" variant="elevated" style={{ minWidth: 220 }}>
                  Highest elevation — borderless.
                </Card>
                <Card heading="Flagged" tone="flagged" style={{ minWidth: 220 }}>
                  A response with at least one validation finding.
                </Card>
                <Card heading="Danger" tone="danger" style={{ minWidth: 220 }}>
                  Critical state — for example, a failed migration banner.
                </Card>
                <Card heading="Warning" tone="warning" style={{ minWidth: 220 }}>
                  Soft warning — stale answers, deprecated form, etc.
                </Card>
                <Card heading="Success" tone="success" style={{ minWidth: 220 }}>
                  Confirmed action — submission accepted, etc.
                </Card>
                <Card heading="Info" tone="info" style={{ minWidth: 220 }}>
                  Informational — async validator pending.
                </Card>
                <Card heading="Accent" tone="accent" style={{ minWidth: 220 }}>
                  Warm accent surface — onboarding, promo.
                </Card>
              </Stack>
            </Card>
          </section>

          {/* Form Errors */}
          <section aria-labelledby="ds-finding-h">
            <Card heading="Form Validation Findings" variant="default">
              <p className="text-sm text-muted-foreground mb-4">
                Message strips indicating warning, info, or validation error states.
              </p>
              <FormError
                findings={[
                  { severity: 'error', code: 'required', message: 'This field is required.' },
                  {
                    severity: 'warning',
                    code: 'stale',
                    message: 'Answer is older than the 180-day freshness threshold.',
                  },
                  { severity: 'info', code: 'async', message: 'Eligibility check still running.' },
                ]}
              />
            </Card>
          </section>
        </Stack>
      ) : (
        <Stack gap={32}>
          {/* Colors */}
          <section aria-labelledby="ds-families-h">
            <Card heading="Colour Families" variant="default">
              <p className="text-sm text-muted-foreground mb-4">
                Each color family carries a base swatch, a foreground color, a muted variant, and a
                subtle wash.
              </p>
              <Stack gap={16}>
                {COLOR_FAMILIES.map((family) => (
                  <FamilyRow key={family} family={family} />
                ))}
              </Stack>
            </Card>
          </section>

          {/* Surfaces */}
          <section aria-labelledby="ds-surface-h">
            <Card heading="Surface Tokens" variant="default">
              <p className="text-sm text-muted-foreground mb-4">
                Backgrounds, borders, and rings for constructing structural layouts.
              </p>
              <Stack direction="row" gap={12} style={{ flexWrap: 'wrap' }}>
                {SURFACE_TOKENS.map((name) => (
                  <SwatchCard key={name} token={name} />
                ))}
              </Stack>
            </Card>
          </section>

          {/* Elevation */}
          <section aria-labelledby="ds-elevation-h">
            <Card heading="Elevation & Shadows" variant="default">
              <p className="text-sm text-muted-foreground mb-4">
                Shadow tokens for structural depth and popovers.
              </p>
              <Stack direction="row" gap={24} style={{ flexWrap: 'wrap' }}>
                {SHADOW_TOKENS.map(({ name, label }) => (
                  <div
                    key={name}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <div
                      style={{
                        width: 120,
                        height: 80,
                        background: 'rgb(var(--color-surface))',
                        border: '1px solid rgb(var(--color-border))',
                        borderRadius: 'var(--radius-md)',
                        boxShadow: `var(--shadow-${name})`,
                      }}
                    />
                    <code style={{ fontSize: 12 }}>--shadow-{name}</code>
                    <span
                      className="text-xs text-muted-foreground text-center"
                      style={{ maxWidth: 140 }}
                    >
                      {label}
                    </span>
                  </div>
                ))}
              </Stack>
            </Card>
          </section>

          {/* Radius */}
          <section aria-labelledby="ds-radius-h">
            <Card heading="Border Radius Scale" variant="default">
              <p className="text-sm text-muted-foreground mb-4">
                Consistent corner rounding tokens.
              </p>
              <Stack direction="row" gap={24} style={{ flexWrap: 'wrap' }}>
                {RADIUS_TOKENS.map(({ name, label }) => (
                  <div
                    key={name}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 8,
                      alignItems: 'center',
                    }}
                  >
                    <div
                      style={{
                        width: 80,
                        height: 80,
                        background: 'rgb(var(--color-primary))',
                        borderRadius: `var(--radius-${name})`,
                      }}
                    />
                    <code style={{ fontSize: 12 }}>--radius-{name}</code>
                    <span
                      className="text-xs text-muted-foreground text-center"
                      style={{ maxWidth: 140 }}
                    >
                      {label}
                    </span>
                  </div>
                ))}
              </Stack>
            </Card>
          </section>

          {/* Gradients */}
          <section aria-labelledby="ds-gradient-h">
            <Card heading="Gradients" variant="default">
              <p className="text-sm text-muted-foreground mb-4">
                Premium visual washes for hero blocks and callouts.
              </p>
              <Stack direction="row" gap={16} style={{ flexWrap: 'wrap' }}>
                {GRADIENT_TOKENS.map(({ name, label }) => (
                  <div
                    key={name}
                    style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 220 }}
                  >
                    <div
                      style={{
                        height: 96,
                        borderRadius: 'var(--radius-lg)',
                        border: '1px solid rgb(var(--color-border))',
                        background: `var(--gradient-${name})`,
                      }}
                    />
                    <code style={{ fontSize: 12 }}>--gradient-{name}</code>
                    <span className="text-xs text-muted-foreground">{label}</span>
                  </div>
                ))}
              </Stack>
            </Card>
          </section>

          {/* Typography */}
          <section aria-labelledby="ds-typography-h">
            <Card heading="Typography & Font scale" variant="default">
              <p className="text-sm text-muted-foreground mb-4">
                Font sizes and display hierarchies.
              </p>
              <Stack gap={12}>
                {TYPE_SCALE.map(({ token, label }) => (
                  <div
                    key={token}
                    style={{
                      fontSize: `var(--text-${token})`,
                      lineHeight: `var(--leading-${token})`,
                      fontFamily:
                        token === '3xl' || token === '2xl' || token === 'xl'
                          ? 'var(--font-display)'
                          : 'var(--font-body)',
                      letterSpacing:
                        token === '3xl' || token === '2xl' ? 'var(--tracking-tight)' : 'normal',
                      fontWeight: token === '3xl' || token === '2xl' || token === 'xl' ? 600 : 400,
                    }}
                  >
                    <code
                      style={{
                        fontSize: 12,
                        marginRight: 12,
                        color: 'rgb(var(--color-muted-foreground))',
                      }}
                    >
                      --text-{token}
                    </code>
                    {label}
                  </div>
                ))}
              </Stack>
            </Card>
          </section>
        </Stack>
      )}
    </div>
  );
}

function FamilyRow({ family }: { family: ColorFamily }) {
  return (
    <div
      style={{
        border: '1px solid rgb(var(--color-border))',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-3)',
        background: 'rgb(var(--color-surface))',
      }}
    >
      <div style={{ marginBottom: 8, fontFamily: 'var(--font-display)', fontWeight: 600 }}>
        {family}
      </div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {FAMILY_VARIANTS.map((variant) => {
          const token = `--color-${family}${variant}`;
          const variantLabel = variant === '' ? 'DEFAULT' : variant.slice(1);
          return (
            <div
              key={token}
              style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 140 }}
            >
              <div
                style={{
                  width: '100%',
                  height: 48,
                  borderRadius: 'var(--radius-sm)',
                  background: `rgb(var(${token}))`,
                  border: '1px solid rgb(var(--color-border))',
                }}
              />
              <code style={{ fontSize: 11 }}>{token}</code>
              <span className="muted" style={{ fontSize: 11 }}>
                {variantLabel}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SwatchCard({ token }: { token: string }) {
  return (
    <div
      style={{
        border: '1px solid rgb(var(--color-border))',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-2) var(--space-3)',
        background: 'rgb(var(--color-surface))',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        minWidth: 140,
      }}
    >
      <div
        aria-hidden="true"
        style={{
          width: '100%',
          height: 32,
          borderRadius: 'var(--radius-sm)',
          background: `rgb(var(--color-${token}))`,
          border: '1px solid rgb(var(--color-border))',
        }}
      />
      <code style={{ fontSize: 11 }}>--color-{token}</code>
    </div>
  );
}
